import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport, type UIMessage } from 'ai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import { DateTime } from 'luxon'
import { useAppDispatch, type ReduxState } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { apiAxios } from '@/api'
import { logHeardError } from '@/utils/errorHelpers'
import { useAdviserAnalytics } from '../analytics'
import {
  selectActiveConversationId,
  selectActiveLaunchAction,
  selectActiveEntryPoint,
} from '../adviser-chat.selectors'
import { setActiveConversationId } from '../adviser-chat.slice'
import { submitFeedback } from '../adviser-chat.actions'
import {
  ADVISER_API_PATH,
  ADVISER_CHAT_URL,
  INITIAL_SOURCE_FETCH_DELAY_MS,
  SOURCE_FETCH_RETRY_DELAY_MS,
  MAX_SOURCE_FETCH_RETRIES,
  AdviserRole,
  AdviserToolName,
  ToolInvocationState,
} from '../lib/constants'
import { isLaunchSentinel } from '../lib/launchContext'
import { createZendeskAttachmentUploader } from '../lib/attachmentUploader'
import {
  answeredToolPart,
  findToolPart,
  getMessageContent,
  getToolInvocations,
  parseTicketFileNames,
  replaceToolPart,
  toChatRole,
  widgetCallIdOf,
} from '../lib/mappers'
import { splitsText } from '../components/widgets/registry'
import { prepareChatRequestBody } from '../lib/prepareChatRequest'
import { addressesHaven } from '../lib/havenMention'
import { adaptOperatorAcknowledgement } from '../lib/operatorAcknowledgement'
import type {
  AdviserMessage,
  AttachedFile,
  CitationSource,
  ConversationSummary,
  FeedbackVote,
  HavenActionLaunchContext,
  WidgetDisplay,
} from '@/features/adviser-chat/lib/types'
import { MAX_ATTACHMENTS, type AttachmentRef } from '../lib/attachments.types'
import { usePageContext } from './usePageContext'

const attachmentUploader = createZendeskAttachmentUploader()

const isImageContentType = (contentType: string): boolean =>
  contentType.startsWith('image/')

const TICKET_WRITING_TOOLS: string[] = [
  AdviserToolName.CreateTicket,
  AdviserToolName.AddTicketComment,
]

interface TicketWriteResult {
  success: boolean
  spentFileNames: string[]
}

const parseTicketWriteResult = (output: unknown): TicketWriteResult | null => {
  let value: unknown = output
  if (typeof value === 'string') {
    try {
      const decoded: unknown = JSON.parse(value)
      value = decoded
    } catch {
      return null
    }
  }
  if (typeof value !== 'object' || value === null) return null

  const { attachedFiles = [], droppedAttachments = [] } =
    parseTicketFileNames(value)
  const spentFileNames = [...attachedFiles, ...droppedAttachments]
  const success =
    'success' in value && typeof value.success === 'boolean' && value.success

  return { success, spentFileNames }
}

const trailingAssistantMessages = (msgs: UIMessage[]): UIMessage[] => {
  let index = msgs.length
  while (index > 0 && msgs[index - 1].role === AdviserRole.Assistant) {
    index -= 1
  }
  return msgs.slice(index)
}

const mergeAttachmentRefs = (
  buffered: AttachmentRef[],
  incoming: AttachmentRef[]
): AttachmentRef[] => {
  const seen = new Set<string>()
  const dedupedIncoming: AttachmentRef[] = []
  for (const ref of incoming) {
    if (seen.has(ref.token)) continue
    seen.add(ref.token)
    dedupedIncoming.push(ref)
  }

  const retainedBuffered: AttachmentRef[] = []
  for (const ref of buffered) {
    if (seen.has(ref.token)) continue
    seen.add(ref.token)
    retainedBuffered.push(ref)
  }

  const room = Math.max(MAX_ATTACHMENTS - dedupedIncoming.length, 0)
  const keptBuffered = room === 0 ? [] : retainedBuffered.slice(-room)
  return [...keptBuffered, ...dedupedIncoming].slice(0, MAX_ATTACHMENTS)
}

export const useAdviserChat = (recentHistoryEnabled = false) => {
  const dispatch = useAppDispatch()
  const store = useStore<ReduxState>()
  const {
    trackConversationStarted,
    trackConversationResumed,
    trackChatFailed,
  } = useAdviserAnalytics()
  const activeConversationId = useReselector(selectActiveConversationId)
  const [sourcesMap, setSourcesMap] = useState<
    Record<string, CitationSource[]>
  >({})
  const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackVote>>(
    {}
  )
  const prevStatus = useRef<string>('')
  const pageContext = usePageContext()
  const pageContextRef = useRef(pageContext)
  pageContextRef.current = pageContext

  const activeLaunchAction = useReselector(selectActiveLaunchAction)
  const activeEntryPoint = useReselector(selectActiveEntryPoint)
  const launchRef = useRef({ activeLaunchAction, activeEntryPoint })
  launchRef.current = { activeLaunchAction, activeEntryPoint }
  // Maps client-side (AI SDK) message IDs to their persisted DB integer IDs.
  // Populated after streaming by the same refetch that hydrates sources; used
  // by feedback submission so just-streamed messages can be rated.
  const clientIdToDbIdRef = useRef<Map<string, number>>(new Map())
  const pendingTurnBaselineRef = useRef<Set<string> | null>(null)
  const getPersistedIdForClientId = useCallback(
    (clientId: string): number | undefined =>
      clientIdToDbIdRef.current.get(clientId),
    []
  )
  const [messageCreatedAtByClientId, setMessageCreatedAtByClientId] = useState<
    Record<string, string>
  >({})
  // The newest stored assistant turn this hook has seen settle. A turn answered on a
  // widget continues one message, so counting messages cannot tell whether the turn that
  // just streamed has been written yet; a newer row id can.
  const settledAssistantIdRef = useRef(0)
  // A launch that continued an existing conversation stays pending until its history reload
  // lands; every stored turn until then offers another chance.
  const pendingResumeRef = useRef<number | null>(null)
  const resumeBaselineRef = useRef(0)
  // Sends, retries and card answers, so a reload can tell the reader acted while it loaded.
  const userTurnsRef = useRef(0)
  const [resumedTurnStored, setResumedTurnStored] = useState<number | null>(
    null
  )
  // Feedback clicks that arrived before the DB id was known. Flushed by the
  // post-stream refetch once the client→DB id mapping is available.
  const pendingFeedbackRef = useRef<Map<string, FeedbackVote>>(new Map())

  const bufferedAttachmentsRef = useRef<AttachmentRef[]>([])
  const sentAttachmentTokensRef = useRef<string[]>([])
  // Written by the widget each render: the API named an operator as the
  // recipient of a plain message in this conversation.
  const operatorHeldRef = useRef(false)

  // Clear the id map and any pending submissions whenever the active
  // conversation changes so state cannot leak across.
  useEffect(() => {
    clientIdToDbIdRef.current = new Map()
    pendingFeedbackRef.current = new Map()
    setMessageCreatedAtByClientId({})
  }, [activeConversationId])

  // The server saves the answer only as the reply starts streaming, so a
  // request that fails before then left the widget unanswered.
  const lastRequestRefused = useRef(false)
  const previewUrlsRef = useRef<string[]>([])
  const revokePreviewUrls = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    previewUrlsRef.current = []
  }, [])

  const previousConversationIdRef = useRef(activeConversationId)
  useEffect(() => {
    const previous = previousConversationIdRef.current
    previousConversationIdRef.current = activeConversationId
    // A null id turning real is this conversation being created by the message
    // just sent, so its previews and attachments have to outlive that transition.
    if (previous !== null && previous !== activeConversationId) {
      pendingTurnBaselineRef.current = null
      unmappedTurnIdsRef.current = new Set()
      pendingResumeRef.current = null
      revokePreviewUrls()
      bufferedAttachmentsRef.current.forEach((ref) => {
        void attachmentUploader.remove(ref).catch((error) => {
          logHeardError(error)
        })
      })
      bufferedAttachmentsRef.current = []
      sentAttachmentTokensRef.current = []
    }
  }, [activeConversationId, revokePreviewUrls])

  useEffect(() => revokePreviewUrls, [revokePreviewUrls])

  const [startedConversation, setStartedConversation] =
    useState<Omit<ConversationSummary, 'userId'>>()
  const acceptedVersionRef = useRef(0)
  const [pendingOperatorAcknowledgement, setPendingOperatorAcknowledgement] =
    useState<{ conversationId: number; version: number } | null>(null)
  const [operatorAcknowledged, setOperatorAcknowledged] = useState<{
    conversationId: number
    version: number
  } | null>(null)

  // Custom fetch wrapper to send credentials and intercept X-Conversation-Id
  const customFetch: typeof fetch = useCallback(
    async (input, init) => {
      let response: Response
      try {
        response = await fetch(input, { ...init, credentials: 'include' })
      } catch (err) {
        lastRequestRefused.current = true
        throw err
      }
      if (!response.ok) lastRequestRefused.current = true
      const convId = response.headers.get('X-Conversation-Id')
      if (convId) {
        const newId = parseInt(convId, 10)
        if (Number.isFinite(newId)) {
          const resumedFrom = parseInt(
            response.headers.get('X-Conversation-Resumed') ?? '',
            10
          )
          const resumed = Number.isFinite(resumedFrom)
          if (resumed) {
            settledAssistantIdRef.current = resumedFrom
            resumeBaselineRef.current = resumedFrom
            pendingResumeRef.current = newId
          }
          const wasNewConversation = conversationIdRef.current === null
          dispatch(setActiveConversationId(newId))
          if (wasNewConversation) {
            const title = response.headers.get('X-Conversation-Title')
            if (title) {
              const now = DateTime.now().toISO()
              setStartedConversation({
                id: newId,
                title: decodeURIComponent(title),
                createdAt: now,
                updatedAt: now,
              })
            }
            const launch = launchRef.current
            const launchProps = {
              launchContext:
                launch.activeLaunchAction ??
                (launch.activeEntryPoint ? 'message' : null),
              entryPoint: launch.activeEntryPoint,
            }
            if (resumed) trackConversationResumed(newId, launchProps)
            else trackConversationStarted(newId, launchProps)
          }
        }
      }
      if (response.status === 202) {
        const acceptedId = Number(convId)
        if (Number.isInteger(acceptedId) && acceptedId > 0) {
          acceptedVersionRef.current += 1
          setPendingOperatorAcknowledgement({
            conversationId: acceptedId,
            version: acceptedVersionRef.current,
          })
        }
        return adaptOperatorAcknowledgement(response)
      }
      return response
    },
    [dispatch, trackConversationStarted, trackConversationResumed]
  )

  const conversationIdRef = useRef(activeConversationId)
  conversationIdRef.current = activeConversationId

  const [transport] = useState(
    () =>
      new DefaultChatTransport({
        api: `${ADVISER_CHAT_URL}/chat`,
        fetch: customFetch,
        prepareSendMessagesRequest: ({ messages: outgoing, body }) => ({
          body: prepareChatRequestBody({
            messages: outgoing,
            conversationId: conversationIdRef.current,
            pageContext: pageContextRef.current,
            // Read from the store, not the ref: the panel opens a sorting
            // conversation and sends in one effect, before the ref re-renders.
            entryPoint: selectActiveEntryPoint(store.getState()),
            extraBody: body,
          }),
        }),
      })
  )

  const handleAdviserError = useCallback(
    (phase: 'send' | 'stream', err: unknown) => {
      pendingTurnBaselineRef.current = null
      unmappedTurnIdsRef.current = new Set()
      const conversationId = conversationIdRef.current
      const errorName =
        err instanceof Error && err.name ? err.name : 'UnknownError'
      logHeardError(err, {
        component: 'adviser-chat',
        phase,
        conversationId,
      })
      trackChatFailed({ conversationId, phase, errorName })
    },
    [trackChatFailed]
  )

  const {
    messages,
    setMessages,
    sendMessage,
    regenerate,
    status,
    error,
    clearError,
    stop,
  } = useChat({
    transport,
    onError: (err) => {
      handleAdviserError('stream', err)
    },
  })

  useEffect(() => {
    if (status !== 'ready' || !pendingOperatorAcknowledgement) return
    setOperatorAcknowledged(pendingOperatorAcknowledgement)
    setPendingOperatorAcknowledgement(null)
  }, [status, pendingOperatorAcknowledgement])

  // Fetch sources after streaming completes, with retry for slow onFinish persistence
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const markTurnPending = useCallback((includeLastMessage = false): void => {
    const baseline = new Set(messagesRef.current.map((message) => message.id))
    if (includeLastMessage) {
      const lastMessage = messagesRef.current.at(-1)
      if (lastMessage) baseline.delete(lastMessage.id)
    }
    pendingTurnBaselineRef.current = baseline
  }, [])
  const isPendingLocalMessage = useCallback(
    (clientId: string): boolean =>
      pendingTurnBaselineRef.current !== null &&
      !pendingTurnBaselineRef.current.has(clientId),
    []
  )
  // A turn whose stream landed but whose source fetch never reached the db
  // leaves its messages local and unmapped for good, and the server already
  // holds copies of them. They stay held back from the restore merge so those
  // copies cannot come back as duplicates -- unlike a failed send, whose
  // messages never reached the server and must not hide anything saved there.
  const unmappedTurnIdsRef = useRef<Set<string>>(new Set())
  const settleUnmappedTurn = useCallback((): void => {
    const baseline = pendingTurnBaselineRef.current
    if (baseline === null) return
    for (const message of messagesRef.current) {
      if (baseline.has(message.id)) continue
      if (clientIdToDbIdRef.current.has(message.id)) continue
      unmappedTurnIdsRef.current.add(message.id)
    }
  }, [])
  const isUnmappedSettledMessage = useCallback(
    (clientId: string): boolean => unmappedTurnIdsRef.current.has(clientId),
    []
  )

  // A Zendesk upload token is single use, so a ref leaves the buffer only once a
  // ticket-writing tool has reported it spent.
  const consumeSpentAttachments = useCallback((msgs: UIMessage[]) => {
    const spentNames = new Set<string>()
    let writeSucceeded = false

    for (const msg of trailingAssistantMessages(msgs)) {
      for (const invocation of getToolInvocations(msg)) {
        if (!TICKET_WRITING_TOOLS.includes(invocation.toolName)) continue
        if (invocation.state !== ToolInvocationState.OutputAvailable) continue
        const parsed = parseTicketWriteResult(invocation.result)
        if (!parsed) continue
        parsed.spentFileNames.forEach((name) => spentNames.add(name))
        if (parsed.success) writeSucceeded = true
      }
    }

    if (!writeSucceeded && spentNames.size === 0) return

    const spentTokens = new Set(
      writeSucceeded ? sentAttachmentTokensRef.current : []
    )
    bufferedAttachmentsRef.current = bufferedAttachmentsRef.current.filter(
      (ref) => !spentTokens.has(ref.token) && !spentNames.has(ref.fileName)
    )
    if (writeSucceeded) {
      sentAttachmentTokensRef.current = []
    }
  }, [])

  useEffect(() => {
    const wasStreaming =
      prevStatus.current === 'streaming' || prevStatus.current === 'submitted'
    const nowReady = status === 'ready'
    prevStatus.current = status

    if (!wasStreaming || !nowReady || pendingOperatorAcknowledgement) {
      return undefined
    }

    consumeSpentAttachments(messagesRef.current)

    if (!activeConversationId) {
      pendingTurnBaselineRef.current = null
      return undefined
    }

    let cancelled = false
    const settledFrom = settledAssistantIdRef.current

    const fetchSources = async (attempt: number): Promise<void> => {
      try {
        const { data } = await apiAxios.get<{
          messages?: AdviserMessage[]
        }>(
          `${ADVISER_API_PATH}/conversations/${activeConversationId}${recentHistoryEnabled ? '?history=recent' : ''}`
        )
        if (cancelled) return
        if (!data) {
          settleUnmappedTurn()
          pendingTurnBaselineRef.current = null
          return
        }

        const dbMessages = data.messages ?? []
        const isRestored = (message: UIMessage): boolean =>
          typeof message.metadata === 'object' &&
          message.metadata !== null &&
          'restored' in message.metadata &&
          message.metadata.restored === true
        const restoredIds = messagesRef.current
          .filter(isRestored)
          .map((message) => Number(message.id))
          .filter(Number.isInteger)
        const lastRestoredId = Math.max(0, ...restoredIds)
        const resumeBaseline =
          pendingResumeRef.current === activeConversationId
            ? resumeBaselineRef.current
            : 0
        const baseline = Math.max(lastRestoredId, resumeBaseline)
        const mappedDbIds = new Set(clientIdToDbIdRef.current.values())
        const dbAssistant = dbMessages.filter(
          (m) => toChatRole(m.role) === AdviserRole.Assistant
        )
        const dbUser = dbMessages.filter(
          (m) => toChatRole(m.role) === AdviserRole.User
        )
        const uiAssistant = messagesRef.current.filter(
          (m) =>
            m.role === AdviserRole.Assistant &&
            !isRestored(m) &&
            !clientIdToDbIdRef.current.has(m.id)
        )
        const uiUser = messagesRef.current.filter(
          (m) =>
            m.role === AdviserRole.User &&
            !isLaunchSentinel(m) &&
            !isRestored(m) &&
            !clientIdToDbIdRef.current.has(m.id)
        )
        const pairableAssistant = dbAssistant.filter(
          (message) => message.id > baseline && !mappedDbIds.has(message.id)
        )
        const pairableUser = dbUser.filter(
          (message) => message.id > baseline && !mappedDbIds.has(message.id)
        )
        const dbAssistantTail = pairableAssistant.slice(-uiAssistant.length)
        const dbUserTail = pairableUser.slice(-uiUser.length)

        const newSourcesMap: Record<string, CitationSource[]> = {}
        const newMessageCreatedAtByClientId: Record<string, string> = {}
        const uiIdByDbId = new Map<number, string>()
        for (let i = 0; i < uiAssistant.length; i++) {
          const dbMsg = dbAssistantTail[i]
          if (!dbMsg) continue
          clientIdToDbIdRef.current.set(uiAssistant[i].id, dbMsg.id)
          uiIdByDbId.set(dbMsg.id, uiAssistant[i].id)
          if (dbMsg.createdAt) {
            newMessageCreatedAtByClientId[uiAssistant[i].id] = dbMsg.createdAt
          }
        }
        for (const dbMsg of dbAssistant) {
          const cited = dbMsg.sources
          if (!Array.isArray(cited) || cited.length === 0) continue
          const callId = widgetCallIdOf(dbMsg, splitsText)
          if (callId) {
            newSourcesMap[callId] = cited
            continue
          }
          const uiId = uiIdByDbId.get(dbMsg.id)
          if (uiId) newSourcesMap[uiId] = cited
        }
        for (let i = 0; i < uiUser.length; i++) {
          const dbMsg = dbUserTail[i]
          if (!dbMsg) continue
          clientIdToDbIdRef.current.set(uiUser[i].id, dbMsg.id)
          if (dbMsg.createdAt) {
            newMessageCreatedAtByClientId[uiUser[i].id] = dbMsg.createdAt
          }
        }

        if (Object.keys(newMessageCreatedAtByClientId).length > 0) {
          setMessageCreatedAtByClientId((current) => ({
            ...current,
            ...newMessageCreatedAtByClientId,
          }))
        }

        const latestAssistantId = dbAssistant[dbAssistant.length - 1]?.id ?? 0
        const latestTurnStored =
          latestAssistantId > settledFrom && latestAssistantId > baseline
        if (latestTurnStored) settledAssistantIdRef.current = latestAssistantId
        if (
          latestTurnStored &&
          pendingResumeRef.current === activeConversationId
        )
          setResumedTurnStored(activeConversationId)

        if (Object.keys(newSourcesMap).length > 0) {
          setSourcesMap((prev) => ({ ...prev, ...newSourcesMap }))
        }

        // Flush any feedback clicks that arrived before the DB id was known.
        // Remove each one from the queue only after a successful persist so
        // a transient failure can be retried on the next refetch tick.
        for (const [clientId, payload] of pendingFeedbackRef.current) {
          const dbId = clientIdToDbIdRef.current.get(clientId)
          if (!dbId) continue
          const convId = activeConversationId
          submitFeedback(convId, {
            messageId: dbId,
            rating: payload.rating,
            comment: payload.comment,
          }).then((ok) => {
            if (ok) {
              pendingFeedbackRef.current.delete(clientId)
            } else {
              setFeedbackMap((prev) => {
                const { [clientId]: _removed, ...next } = prev
                return next
              })
              pendingFeedbackRef.current.delete(clientId)
            }
          })
        }

        // Retry if DB hasn't caught up yet (onFinish still persisting)
        if (latestTurnStored || attempt >= MAX_SOURCE_FETCH_RETRIES) {
          if (!latestTurnStored) settleUnmappedTurn()
          pendingTurnBaselineRef.current = null
        } else if (!cancelled) {
          setTimeout(
            () => fetchSources(attempt + 1),
            SOURCE_FETCH_RETRY_DELAY_MS
          )
        }
      } catch {
        // Source fetch is non-critical; retry on next attempt
        if (cancelled) return
        if (attempt < MAX_SOURCE_FETCH_RETRIES) {
          setTimeout(
            () => fetchSources(attempt + 1),
            SOURCE_FETCH_RETRY_DELAY_MS
          )
        } else {
          settleUnmappedTurn()
          pendingTurnBaselineRef.current = null
        }
      }
    }

    // Initial delay to let onFinish persist the assistant message + sources to DB
    const timer = setTimeout(
      () => fetchSources(0),
      INITIAL_SOURCE_FETCH_DELAY_MS
    )
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    status,
    activeConversationId,
    consumeSpentAttachments,
    settleUnmappedTurn,
    recentHistoryEnabled,
    pendingOperatorAcknowledgement,
  ])

  const handleSendMessage = useCallback(
    (
      content: string,
      options?: {
        pendingAttachments?: AttachmentRef[]
        attachmentFiles?: File[]
      }
    ) => {
      userTurnsRef.current += 1
      const pendingAttachments = options?.pendingAttachments ?? []
      const attachmentFiles = options?.attachmentFiles ?? []
      const addressHaven = addressesHaven(content)
      // An untagged reply to a held thread takes the server's acceptance route,
      // which refuses attachments. Withhold them so the reply still reaches the
      // operator; they stay buffered for the next turn Haven answers.
      const withholdAttachments = operatorHeldRef.current && !addressHaven
      const attachedFiles: AttachedFile[] = withholdAttachments
        ? []
        : pendingAttachments.map((ref, index) => {
            const file = attachmentFiles[index]
            const contentType = ref.contentType || file?.type || ''
            if (!file || !isImageContentType(contentType)) {
              return { fileName: ref.fileName, contentType }
            }
            const previewUrl = URL.createObjectURL(file)
            previewUrlsRef.current.push(previewUrl)
            return { fileName: ref.fileName, contentType, previewUrl }
          })
      const outgoingAttachments = mergeAttachmentRefs(
        bufferedAttachmentsRef.current,
        pendingAttachments
      )
      bufferedAttachmentsRef.current = outgoingAttachments
      const sentAttachments = withholdAttachments ? [] : outgoingAttachments
      sentAttachmentTokensRef.current = sentAttachments.map(
        ({ token }) => token
      )

      const requestBody = {
        ...(sentAttachments.length
          ? { pendingAttachments: sentAttachments }
          : {}),
        ...(addressHaven ? { addressHaven: true } : {}),
      }

      markTurnPending()
      sendMessage(
        attachedFiles.length
          ? { text: content, metadata: { attachedFiles } }
          : { text: content },
        sentAttachments.length || addressHaven
          ? { body: requestBody }
          : undefined
      ).catch((err) => {
        handleAdviserError('send', err)
      })
    },
    [sendMessage, handleAdviserError, markTurnPending]
  )

  // `regenerate` resends the turn without the per-request body the original
  // send carried, so a retry would drop the files while their previews stay
  // in the conversation.
  const handleRetry = useCallback(() => {
    userTurnsRef.current += 1
    const lastUserMessage = [...messagesRef.current]
      .reverse()
      .find((message) => message.role === AdviserRole.User)
    const addressHaven = lastUserMessage
      ? addressesHaven(getMessageContent(lastUserMessage))
      : false
    const outgoing =
      operatorHeldRef.current && !addressHaven
        ? []
        : bufferedAttachmentsRef.current
    markTurnPending(true)
    regenerate(
      outgoing.length || addressHaven
        ? {
            body: {
              ...(outgoing.length ? { pendingAttachments: outgoing } : {}),
              ...(addressHaven ? { addressHaven: true } : {}),
            },
          }
        : undefined
    ).catch((err) => {
      handleAdviserError('send', err)
    })
  }, [regenerate, handleAdviserError, markTurnPending])

  const launchWithContext = useCallback(
    (context: HavenActionLaunchContext) => {
      markTurnPending()
      sendMessage({ text: '', metadata: { launchContext: context } }).catch(
        (err) => {
          handleAdviserError('send', err)
        }
      )
    },
    [sendMessage, handleAdviserError, markTurnPending]
  )

  const submitWidgetOutcome = useCallback(
    async (
      invocation: { toolCallId: string; toolName: string },
      text: string,
      display?: WidgetDisplay
    ): Promise<boolean> => {
      userTurnsRef.current += 1
      markTurnPending(true)
      // The browser's copy of the turn is redrawn whenever the transcript remounts,
      // so the answer has to live there, not only in the widget's own state.
      const open = findToolPart(messagesRef.current, invocation.toolCallId)
      if (open) {
        const answered = answeredToolPart(
          { ...invocation, input: 'input' in open ? open.input : undefined },
          text,
          display
        )
        setMessages((current) =>
          replaceToolPart(current, invocation.toolCallId, answered)
        )
      }
      // Pinned rather than read when the request lands: the customer can switch
      // conversations while the turn is in flight, and the answer belongs here.
      lastRequestRefused.current = false
      await sendMessage(undefined, {
        body: {
          toolOutcome: { ...invocation, text, ...(display ? { display } : {}) },
          ...(conversationIdRef.current
            ? { conversationId: conversationIdRef.current }
            : {}),
        },
      })
      if (open && lastRequestRefused.current) {
        setMessages((current) =>
          replaceToolPart(current, invocation.toolCallId, open)
        )
      }
      return !lastRequestRefused.current
    },
    [sendMessage, setMessages, markTurnPending]
  )

  const consumeResumedTurn = useCallback(() => setResumedTurnStored(null), [])
  const clearPendingResume = useCallback(() => {
    pendingResumeRef.current = null
  }, [])

  const handleFeedback = useCallback(
    async (messageId: string, rating: -1 | 1, comment?: string) => {
      if (!activeConversationId) return

      // Optimistic UI update first so the thumb reflects instantly.
      setFeedbackMap((prev) => ({ ...prev, [messageId]: { rating, comment } }))

      // Resolve the UI message id to its persisted DB integer id. Resumed
      // conversations already carry the DB id as a numeric string; live-
      // streamed messages use AI SDK client ids and require the map
      // populated by the post-streaming refetch.
      const mapped = clientIdToDbIdRef.current.get(messageId)
      const parsed = Number.parseInt(messageId, 10)
      const dbMessageId =
        mapped ??
        (Number.isInteger(parsed) && String(parsed) === messageId && parsed > 0
          ? parsed
          : null)

      if (dbMessageId === null) {
        // DB id not yet known — queue the click; the post-stream refetch
        // will flush it once the mapping resolves.
        pendingFeedbackRef.current.set(messageId, { rating, comment })
        return
      }

      const ok = await submitFeedback(activeConversationId, {
        messageId: dbMessageId,
        rating,
        comment,
      })
      if (!ok) {
        setFeedbackMap((prev) => {
          const { [messageId]: _removed, ...next } = prev
          return next
        })
      }
    },
    [activeConversationId]
  )

  return {
    messages,
    messageCreatedAtByClientId,
    getPersistedIdForClientId,
    isPendingLocalMessage,
    isUnmappedSettledMessage,
    setMessages,
    sendMessage: handleSendMessage,
    launchWithContext,
    submitWidgetOutcome,
    stop,
    isStreaming: status === 'streaming' || status === 'submitted',
    error: error ? 'Something went wrong. Please try again.' : null,
    clearError,
    retry: handleRetry,
    handleFeedback,
    sourcesMap,
    setSourcesMap,
    feedbackMap,
    setFeedbackMap,
    startedConversation,
    resumedTurnStored,
    consumeResumedTurn,
    clearPendingResume,
    userTurnsRef,
    operatorAcknowledged,
    operatorHeldRef,
  }
}
